import { type CSSProperties } from "react";

/**
 * `<EnvSwitchOverlay>` — the branded loading overlay rendered
 * RIGHT BEFORE `window.location.assign()` during an env switch or
 * a landing→portal redirect.
 *
 * Why this component exists (CPF-NFR-028)
 * ---------------------------------------
 * Multi-Amplify topology (CPF-NFR-025) means env-switch and
 * post-login redirects are cross-origin navigations. Without
 * mitigation the customer sees a 1-3 second white browser tab
 * during the unload→load transition. Strategy from PORTAL-BACKEND-
 * PLAN.md §0.13.7:
 *
 *   1. Preconnect hints (DNS+TLS warm) — handled by HTML <head>
 *   2. **This component** — last frame the customer sees pre-unload
 *      is BRANDED, not a half-rendered SPA
 *   3. Shared SPA shell — destination hydrates from cache
 *   4. Future: service worker pre-caching
 *
 * The component MUST be rendered + visually painted BEFORE the
 * caller invokes `window.location.assign(targetUrl)`. The standard
 * pattern in the caller is:
 *
 *   const switchEnv = (target: string) => {
 *     setSwitching(true);
 *     requestAnimationFrame(() => {
 *       requestAnimationFrame(() => {
 *         window.location.assign(target);
 *       });
 *     });
 *   };
 *
 * Two RAFs ensure the browser actually paints the overlay before
 * the navigation begins (one RAF = layout commit, two RAFs = paint
 * commit). 50ms `setTimeout` is an alternative but RAFs are more
 * reliable across browsers.
 *
 * Styling note: deliberately uses INLINE STYLES instead of
 * Tailwind / className. This component might render in contexts
 * where the host SPA's CSS isn't loaded yet (e.g. inside an Amplify
 * cold-boot before main.css cascades). Inline styles guarantee the
 * brand renders correctly regardless.
 *
 * Accessibility: `aria-live="polite"` so screen readers announce
 * the env switch without interrupting the user's current speech
 * stream. `role="status"` is the WAI-ARIA correct role for a
 * dynamic-update region that's not a critical alert.
 */

export type EnvSwitchOverlayProps = {
  /**
   * The destination env label rendered in the overlay. Examples:
   * `"Production"`, `"Sandbox"`, `"Development"`. The component
   * does NOT validate; the caller knows the human-readable label.
   */
  targetEnvLabel: string;

  /**
   * Brand accent color for the spinner ring. Defaults to the ROOK
   * brand purple `#831DF5` (from foundations/BRAND-PALETTE.md);
   * overridable for env-color emphasis (red for prod, emerald
   * for sandbox, blue for dev) when the caller wants the spinner
   * to match the destination's env color per CPF-UIR-089.
   */
  brandColor?: string;
};

const containerStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 2147483647, // Max int32 — guarantees overlay sits above any modal/portal
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "1.5rem",
  backgroundColor: "#0a0a0a",
  color: "#f0f0f0",
  fontFamily:
    'Montserrat, "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  // Smooth fade-in so the overlay doesn't pop harshly on already-
  // painted content. 100ms is fast enough to feel instant but
  // long enough that screen readers don't trip.
  animation: "rook-overlay-fade-in 100ms ease-out",
};

const titleStyle: CSSProperties = {
  fontSize: "1.5rem",
  fontWeight: 600,
  letterSpacing: "-0.02em",
  margin: 0,
};

const labelStyle: CSSProperties = {
  fontSize: "0.875rem",
  color: "#a0a0a0",
  margin: 0,
};

const spinnerBaseStyle: CSSProperties = {
  width: "2.5rem",
  height: "2.5rem",
  borderRadius: "50%",
  borderStyle: "solid",
  borderWidth: "3px",
  borderColor: "rgba(255, 255, 255, 0.1)",
  borderTopColor: "var(--rook-spinner-color)",
  animation: "rook-overlay-spin 800ms linear infinite",
};

/**
 * Idempotently ensure the keyframes <style> tag exists in
 * document.head. Called from the component body — the
 * `getElementById` guard means it's a no-op on subsequent renders
 * unless the <style> was removed (which happens in tests + during
 * hot-reload). Guarded for SSR (`typeof document`).
 *
 * Why not module-load-time injection: vitest's afterEach cleanup
 * can remove the <style>, and the next render's guard would still
 * see "injected" from a stale module-level flag → keyframes
 * silently missing. Per-render guard against the live DOM is the
 * robust pattern.
 */
const ensureKeyframesInjected = (): void => {
  if (typeof document === "undefined") return;
  const styleId = "rook-env-switch-overlay-keyframes";
  if (document.getElementById(styleId)) return;
  const styleEl = document.createElement("style");
  styleEl.id = styleId;
  styleEl.textContent = `
    @keyframes rook-overlay-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes rook-overlay-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;
  document.head.appendChild(styleEl);
};

export const EnvSwitchOverlay = ({
  targetEnvLabel,
  brandColor = "#831DF5",
}: EnvSwitchOverlayProps) => {
  ensureKeyframesInjected();

  const spinnerStyle: CSSProperties = {
    ...spinnerBaseStyle,
    // CSS custom property so the keyframe rule (which can't
    // interpolate React props directly) picks up the dynamic
    // border-top-color via var() lookup.
    ["--rook-spinner-color" as string]: brandColor,
  };

  return (
    <div
      style={containerStyle}
      role="status"
      aria-live="polite"
      aria-label={`Switching to ${targetEnvLabel}`}
      data-testid="env-switch-overlay"
    >
      <div style={spinnerStyle} aria-hidden="true" />
      <h2 style={titleStyle}>ROOK Clients Portal</h2>
      <p style={labelStyle}>Switching to {targetEnvLabel}…</p>
    </div>
  );
};
